import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCardTitle,
} from "mdb-react-ui-kit";
import { useSignIn, useSignOut } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDecision } from "@optimizely/react-sdk";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { axios } from "../lib/request";
import { dashboardQueryKey } from "../lib/refresh";
import { Button, Row } from "../App.styles";
import { FormGroup, GoogleButtonWrapper, SignInActionsContainer, SigninForm } from "./SignIn.styles";
import { Alert, Spinner } from "../common";
import AlertDisplay from "../common/AlertDisplay";
import { Input } from "../common/Input";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import withDashboardRedirect from "../hoc/withDashboardRedirect";
import {
  buildSignInToken,
  signInState,
  signInWithApple,
  signInWithGoogle,
} from './SignIn.state'
import { removeAuthStorages } from '../lib/auth'
import { ETERNAL_REFRESH_TOKEN } from "../consts/auth";

/**
 * The Sign In component.
 * Renders the login form and handles authentication via email/password,
 * Google, or Apple. Manages auth tokens, redirects, and error states.
 * 
 * @returns {React.ReactElement} The rendered sign-in form.
 */
export const SignIn = () => {
  const { t } = useTranslation();
  const [decisionSocialSignIn] = useDecision("social-sign-in", { autoUpdate: true });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const googleButtonRef = useRef(null);
  const location = useLocation();
  const [error, setError] = useState(
    location?.state?.invalidCode
      ? t("Invalid code. Please try again.")
      : location.search.match(/expired/)
        ? t("Your session has expired and for security purposes we require you to sign in again.")
        : undefined
  );
  const history = useHistory();
  const [, setSignIn] = useRecoilState(signInState);
  const signIn = useSignIn();
  const signOut = useSignOut();
  const queryClient = useQueryClient();
  const setAlert = useSetRecoilState(alertState);
  const handleOnError = useCallback(error => {
    switch (error?.response?.status) {
      case 401:
      case 404:
        setError(
          t("We were unable to sign you in using that email address.")
        );
        break;
      case 500:
        setError(
          t("There has been a problem connecting with our platform. Please try again.")
        );
        break;
      default:
        setError(error?.message || t("There has been a problem connecting with our platform. Please try again."));
    }
  }, [t])

  const handleSignInSuccess = useCallback((data, userId = email) => {
    const authState = buildSignInToken(data, userId)

    // Eternally store refresh token
    localStorage.setItem(ETERNAL_REFRESH_TOKEN, authState?.refreshToken)

    if (
      signIn(authState)
    ) {
      setSignIn(uuidv4());
      window.dispatchEvent(new StorageEvent("auth-storage"))
      setError(null);
      queryClient.invalidateQueries(dashboardQueryKey());
      let redirectTo = location.state?.referrer
      if (!redirectTo || redirectTo === '//') {
        redirectTo = { pathname: "/dashboard" }
      }
      history.replace(redirectTo);
    } else {
      console.error("signIn error");
    }
  }, [email, history, location.state?.referrer, queryClient, setSignIn, signIn])

  const { isLoading, mutate } = useMutation(
    (params) => axios.post(process.env.REACT_APP_API_AUTH_URL, params).catch(error => {
      return { error }
    }),
    {
      onError: handleOnError,
      onSuccess: ({ data, error }) => {
        if (error) {
          handleOnError(error)
          return
        }

        handleSignInSuccess(data, email)
      },
    }
  );

  const { isLoading: isGoogleLoading, mutate: mutateGoogle } = useMutation(
    (idToken) => signInWithGoogle(idToken).catch(error => {
      return { error }
    }),
    {
      onError: handleOnError,
      onSuccess: ({ data, error }) => {
        if (error) {
          handleOnError(error)
          return
        }

        handleSignInSuccess(data)
      },
    }
  );

  const { isLoading: isAppleLoading, mutate: mutateApple } = useMutation(
    (params) => signInWithApple(params).catch(error => {
      return { error }
    }),
    {
      onError: handleOnError,
      onSuccess: ({ data, error }) => {
        if (error) {
          handleOnError(error)
          return
        }

        handleSignInSuccess(data)
      },
    }
  );

  const googleClientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;
  const appleServicesId = process.env.REACT_APP_APPLE_SERVICES_ID;
  const appleRedirectUri = process.env.REACT_APP_APPLE_REDIRECT_URI;
  const appleSignInUrl = useMemo(() => {
    if (!appleServicesId || !appleRedirectUri) {
      return null;
    }

    const stateObj = {
      redirect_url: window.location.origin
    };

    const params = new URLSearchParams({
      client_id: appleServicesId,
      redirect_uri: appleRedirectUri,
      response_mode: "form_post",
      response_type: "code",
      scope: "name email",
      state: btoa(JSON.stringify(stateObj)),
      nonce: uuidv4().replace(/-/g, ""),
    })

    return `https://appleid.apple.com/auth/authorize?${params.toString()}`
  }, [appleRedirectUri, appleServicesId])

  useEffect(() => {
    if (email || password) {
      setError(null);
    }
  }, [email, location, password]);

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) {
      return
    }

    const initializeGoogleSignIn = () => {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: ({ credential }) => {
          if (credential) {
            mutateGoogle(credential)
          }
        },
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
      });
    }

    if (window.google?.accounts?.id) {
      initializeGoogleSignIn()
      return
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignIn;
    document.body.appendChild(script);
  }, [googleClientId, mutateGoogle, decisionSocialSignIn?.enabled]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (code) {
      mutateApple({ code, state })
    }
  }, [location.search, mutateApple]);

  useEffect(() => {
    if (location?.state?.resetPassword) {
      setAlert({
        type: "success",
        message: t("A new password has been sent to your email address."),
      })
    } else if (location?.state?.createAccount) {
        setAlert({
            type: "success",
            message: t("Your account was created successfully, please sign in."),
        })
    }
  }, [
    location?.state?.resetPassword, 
    location?.state?.createAccount, 
    setAlert, 
    t
  ])

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("token") && params.get("refresh_token")) {
      return;
    }

    signOut();
    setSignIn(null);
    removeAuthStorages();
    window.dispatchEvent(new StorageEvent("auth-storage"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Row>
        <MDBCol size="12">
          <Alert color="error">{error}</Alert>
          <AlertDisplay />
        </MDBCol>
      </Row>
      <Row>
        <MDBCol size="12">
          <MDBCard>
            <MDBCardHeader>
              <MDBCardTitle>
                <span dangerouslySetInnerHTML={{__html: t('Sign in')}} />
              </MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
              <SigninForm
                data-testid="form-signin"
                onSubmit={(e) => {
                  e.preventDefault();
                  mutate({ email, password });
                }}
              >
                <FormGroup>
                  <Input
                    data-testid="form-signin__field-email"
                    className="form-control active"
                    label={t('Email')}
                    value={email}
                    onChange={(value) => setEmail(value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    data-testid="form-signin__field-password"
                    className="form-control active"
                    label={t('Password')}
                    type="password"
                    value={password}
                    onChange={(value) => setPassword(value)}
                  />
                </FormGroup>
                <SignInActionsContainer gap="none">
                  <Button data-testid="form-signin__btn-signin" color="primary" type="submit">
                    <span dangerouslySetInnerHTML={{__html: t('Sign in')}} /> {isLoading && <Spinner size="0.875rem" />}
                  </Button>
                  <Link to="/sign-up">
                    <Button tag="span" color="link">{t('Create Account')}</Button>
                  </Link>
                  <Link to="/reset-password">
                    <Button tag="span" color="link">{t('Reset Password')}</Button>
                  </Link>
                </SignInActionsContainer>
                {decisionSocialSignIn?.enabled && (
                  <SignInActionsContainer>
                    {googleClientId && (
                      <GoogleButtonWrapper
                        data-testid="form-signin__btn-google"
                        ref={googleButtonRef}
                      />
                    )}
                    {appleSignInUrl && (
                      <Button
                        data-testid="form-signin__btn-apple"
                        color="dark"
                        type="button"
                        onClick={() => window.location.assign(appleSignInUrl)}
                        disabled={isAppleLoading}
                      >
                        <FontAwesomeIcon icon={faApple} /> {t("Sign in with Apple")} {isAppleLoading && <Spinner size="0.875rem" />}
                      </Button>
                    )}
                    {isGoogleLoading && <Spinner size="0.875rem" />}
                  </SignInActionsContainer>
                )}
              </SigninForm>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </Row>
    </>
  );
};

export default withDashboardRedirect(SignIn);
